import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import type { PageKey, TicketRow } from "@owners/shared-types";
import { LEVEL_LABELS, PAGE_LABELS, TICKET_STATUS_LABELS } from "@owners/shared-types";
import { api, useMe, usePrefs, type Prefs } from "../api";
import { PATHS } from "../routes";

/**
 * Settings (demo tabs): Notifications, Default views, Reports & delivery,
 * Approvals, Account & security, Help Desk. Default views and the report
 * format are stored per person (V019 user_preferences).
 */
type Tab = "notifications" | "views" | "reports" | "approvals" | "account" | "help";

export function Settings() {
  const { data: me } = useMe();
  const [tab, setTab] = useState<Tab>("notifications");
  const approver = !!me?.capabilities.includes("approve_spend");
  const tabs: [Tab, string][] = [
    ["notifications", "Notifications"], ["views", "Default views"], ["reports", "Reports & delivery"],
    ...(approver ? [["approvals", "Approvals"] as [Tab, string]] : []),
    ["account", "Account & security"], ["help", "Help Desk"],
  ];
  return (
    <>
      <div className="eyebrow">Owners Module · Settings</div>
      <h1>Settings</h1>
      <div className="tabs">
        {tabs.map(([k, l]) => <button key={k} className={tab === k ? "on" : ""} onClick={() => setTab(k)}>{l}</button>)}
      </div>
      {tab === "notifications" && <Notifications />}
      {tab === "views" && me && <DefaultViews pages={me.pages} />}
      {tab === "reports" && <ReportsDelivery />}
      {tab === "approvals" && (
        <div className="card">
          <table><tbody>
            <tr><th>Your approval limit</th><td>$25,000 <span className="muted">· set by the management agreement</span></td></tr>
          </tbody></table>
          <p className="muted">Spend over the limit goes to the next approver. Purchase orders arrive in Awaiting Approval once the Academy feed is connected.</p>
        </div>
      )}
      {tab === "account" && me && (
        <div className="card">
          <table>
            <tbody>
              <tr><th>Name</th><td>{me.name}</td></tr>
              <tr><th>Email</th><td>{me.email}</td></tr>
              <tr><th>Company</th><td>{me.company ?? "—"}</td></tr>
              <tr><th>Access level</th><td>{LEVEL_LABELS[me.level]}</td></tr>
            </tbody>
          </table>
          <p className="muted">Password and two-step sign-in are managed on the sign-in page.</p>
        </div>
      )}
      {tab === "help" && (me?.level === "admin" ? <p className="muted">Admins answer help requests in the Admin panel.</p> : <HelpDesk />)}
    </>
  );
}

const START_PAGES: PageKey[] = ["todays_update", "flash_reports", "asset_management", "financial_reports", "debt_compliance",
  "budget_reforecast", "trends", "my_reports", "missing_data", "receiver_reports"];
const FLASH: [NonNullable<Prefs["flashPeriod"]>, string][] = [["yesterday", "Yesterday"], ["last_week", "Last week"], ["last_month", "Last month"], ["ytd", "Year to date"], ["t12", "Trailing 12 months"]];
const PNL: [NonNullable<Prefs["pnlView"]>, string][] = [["current_month", "Current month"], ["last_month", "Last month"], ["ytd", "Year to date"], ["prior_year_end", "Last year end"]];

/** The whole prefs object is saved at once (the API replaces it), so each tab merges into what's stored. */
function useSavePrefs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: Prefs) => api<{ prefs: Prefs }>("/api/me/preferences", { method: "PUT", body: p }),
    onSuccess: (r) => qc.setQueryData(["prefs"], r.prefs),
  });
}

function DefaultViews({ pages }: { pages: PageKey[] }) {
  const prefs = usePrefs().data ?? {};
  const save = useSavePrefs();
  const set = (k: keyof Prefs, v: string) => save.mutate({ ...prefs, [k]: v });
  return (
    <div className="card">
      <p className="muted" style={{ marginTop: 0 }}>Where you start and what each page opens on. You can still change any page while you're on it.</p>
      <table className="settings-form"><tbody>
        <tr><th>Start page</th><td>
          <select value={prefs.home ?? "todays_update"} disabled={save.isPending} onChange={(e) => set("home", e.target.value)}>
            {START_PAGES.filter((p) => pages.includes(p)).map((p) => <option key={p} value={p}>{PAGE_LABELS[p]}</option>)}
          </select>
          <div className="muted">Opens after you sign in.</div>
        </td></tr>
        {pages.includes("flash_reports") && <tr><th>Flash Reports period</th><td>
          <select value={prefs.flashPeriod ?? "yesterday"} disabled={save.isPending} onChange={(e) => set("flashPeriod", e.target.value)}>
            {FLASH.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </td></tr>}
        {pages.includes("financial_reports") && <tr><th>Profit &amp; loss period</th><td>
          <select value={prefs.pnlView ?? "last_month"} disabled={save.isPending} onChange={(e) => set("pnlView", e.target.value)}>
            {PNL.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </td></tr>}
      </tbody></table>
      {save.isSuccess && <p className="muted">Saved.</p>}
      {save.error && <p className="error">{(save.error as Error).message}</p>}
    </div>
  );
}

function ReportsDelivery() {
  const { data: me } = useMe();
  const prefs = usePrefs().data ?? {};
  const save = useSavePrefs();
  const can = !!me?.pages.includes("my_reports");
  return (
    <div className="card">
      {!can ? <p className="muted" style={{ marginTop: 0 }}>Your access level doesn't include My Reports.</p> : (
        <>
          <table className="settings-form"><tbody>
            <tr><th>File format</th><td>
              <div className="seg">
                {([["pdf", "PDF"], ["xlsx", "Excel"], ["both", "PDF + Excel"]] as const).map(([k, l]) => (
                  <button key={k} disabled={save.isPending} className={(prefs.reportFormat ?? "pdf") === k ? "on" : ""}
                    onClick={() => save.mutate({ ...prefs, reportFormat: k })}>{l}</button>
                ))}
              </div>
              <div className="muted">What My Reports builds unless you pick otherwise.</div>
            </td></tr>
            <tr><th>Saved reports</th><td><Link to={PATHS.my_reports}>Open My Reports</Link> <span className="muted">· every package you've built, ready to download again</span></td></tr>
            <tr><th>Scheduled delivery</th><td className="muted">Not available yet — build a package in My Reports when you need one.</td></tr>
          </tbody></table>
          {save.error && <p className="error">{(save.error as Error).message}</p>}
        </>
      )}
    </div>
  );
}

/** Emails say only that something needs you, with a link — never the document or the figures. */
function Notifications() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["notifications"], queryFn: () => api<{ emailOn: boolean; email: string; kinds: Array<{ kind: string; label: string; email: boolean }> }>("/api/me/notifications") });
  const set = useMutation({
    mutationFn: (b: { kind: string; email: boolean }) => api("/api/me/notifications", { method: "PUT", body: b }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
  const d = q.data;
  if (!d) return <p className="muted">Loading…</p>;
  return (
    <div className="card">
      <p className="muted" style={{ marginTop: 0 }}>
        Emails go to <strong>{d.email}</strong>. They tell you something needs you and link to the page — documents and figures are never in the email.
        {!d.emailOn && <strong> Email isn't switched on for the module yet; your choices are kept for when it is.</strong>}
      </p>
      {!d.kinds.length ? <p className="muted">There are no emails for your access level.</p> : (
        <table><tbody>{d.kinds.map((k) => (
          <tr key={k.kind}><td>{k.label}</td>
            <td className="num"><label style={{ fontSize: 13 }}><input type="checkbox" checked={k.email} disabled={set.isPending}
              onChange={(e) => set.mutate({ kind: k.kind, email: e.target.checked })} /> Email me</label></td></tr>
        ))}</tbody></table>
      )}
      {set.error && <p className="error">{(set.error as Error).message}</p>}
    </div>
  );
}

function HelpDesk() {
  const qc = useQueryClient();
  const tickets = useQuery({ queryKey: ["my-tickets"], queryFn: () => api<{ tickets: TicketRow[] }>("/api/help/tickets") });
  const [topic, setTopic] = useState("");
  const [body, setBody] = useState("");
  const create = useMutation({
    mutationFn: () => api("/api/help/tickets", { method: "POST", body: { topic, body } }),
    onSuccess: () => { setTopic(""); setBody(""); qc.invalidateQueries({ queryKey: ["my-tickets"] }); },
  });

  return (
    <>
      <div className="card">
        <h2>Ask for help</h2>
        <div className="field"><label htmlFor="t">Topic</label><input id="t" value={topic} maxLength={200} onChange={(e) => setTopic(e.target.value)} /></div>
        <div className="field"><label htmlFor="b">Message</label><textarea id="b" rows={4} value={body} onChange={(e) => setBody(e.target.value)} /></div>
        {create.error && <p className="error">{(create.error as Error).message}</p>}
        <button className="btn" disabled={!topic.trim() || !body.trim() || create.isPending} onClick={() => create.mutate()}>Send</button>
      </div>
      <div className="card">
        <h2>Your requests</h2>
        {tickets.data?.tickets.length === 0 && <p className="muted">None yet.</p>}
        {tickets.data?.tickets.map((t) => <Ticket key={t.id} t={t} />)}
      </div>
    </>
  );
}

function Ticket({ t }: { t: TicketRow }) {
  const qc = useQueryClient();
  const [reply, setReply] = useState("");
  const send = useMutation({
    mutationFn: () => api(`/api/help/tickets/${t.id}/messages`, { method: "POST", body: { body: reply } }),
    onSuccess: () => { setReply(""); qc.invalidateQueries({ queryKey: ["my-tickets"] }); },
  });
  return (
    <div style={{ marginBottom: 16 }}>
      <div className="row"><strong>#{t.number} · {t.topic}</strong><span className={`pill ${t.status}`}>{TICKET_STATUS_LABELS[t.status]}</span></div>
      <div className="thread">
        {t.messages.map((m) => (
          <div key={m.id} className={`msg ${m.fromAdmin ? "admin" : ""}`}>
            <div className="muted" style={{ fontSize: 12 }}>{m.authorName} · {new Date(m.createdAt).toLocaleString()}</div>
            <div style={{ whiteSpace: "pre-wrap" }}>{m.body}</div>
          </div>
        ))}
      </div>
      {t.status !== "resolved" && (
        <div className="row">
          <input style={{ flex: 1 }} placeholder="Reply" value={reply} onChange={(e) => setReply(e.target.value)} />
          <button className="btn secondary" disabled={!reply.trim() || send.isPending} onClick={() => send.mutate()}>Reply</button>
        </div>
      )}
    </div>
  );
}
